import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { clerkClient } from "@/App";
import { useStore } from "@/store/store";

export interface AircraftPosition {
  // Basic identification
  hex: string; // ICAO 24-bit address
  flight: string; // Flight number
  r: string; // Registration number
  t: string; // Aircraft type

  // Position and movement
  lat: number; // Latitude
  lon: number; // Longitude
  alt_baro: number; // Barometric altitude in feet
  alt_geom: number; // Geometric altitude in feet
  gs: number; // Ground speed in knots
  track: number; // Track angle in degrees
  baro_rate: number; // Vertical rate in feet per minute

  // Flight data
  category: string; // Aircraft category
  squawk: string; // Transponder code
  emergency: string; // Emergency status

  // Additional flight parameters
  ias?: number; // Indicated airspeed
  tas?: number; // True airspeed
  mach?: number; // Mach number
  true_heading?: number; // True heading
  mag_heading?: number; // Magnetic heading
  roll?: number; // Roll angle
  nav_altitude_mcp?: number; // Selected altitude
  nav_heading?: number; // Selected heading

  // Environmental
  oat?: number; // Outside air temperature
  tat?: number; // Total air temperature
  wd?: number; // Wind direction
  ws?: number; // Wind speed

  // Quality indicators
  seen: number; // Time since last update
  seen_pos: number; // Time since last position update
  rssi: number; // Signal strength
  messages: number; // Total messages received

  // Distance information
  dst: number; // Distance from receiver
  dir: number; // Direction from receiver
}

export interface AircraftPositionsParams {
  lat: number;
  lon: number;
  radius: number;
}

export const fetchAircraftPositions = async (
  params: AircraftPositionsParams
): Promise<AircraftPosition[]> => {
  try {
    const authToken = await clerkClient.session?.getToken();

    const queryParams = new URLSearchParams();
    queryParams.append("lat", params.lat.toString());
    queryParams.append("lon", params.lon.toString());
    queryParams.append("radius", params.radius.toString());

    const queryString = queryParams.toString();

    const resp = await api.get(`/v0/aircraft/positions?${queryString}`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    return resp.data;
  } catch {
    return [];
  }
};

export const useAircraftPositionsQuery = (
  params: AircraftPositionsParams | undefined
) => {
  const shouldShowAircraft = useStore((state) => state.shouldShowAircraft);

  return useQuery({
    queryKey: ["aircraftPositions", params],
    queryFn: () => fetchAircraftPositions(params!),
    enabled: !!params && shouldShowAircraft,
    refetchInterval: 8000, // Refetch every 8 seconds
  });
};
